import { useEffect } from "react";
import type { ProductData } from "@/mocks/products";

interface Props {
  product: ProductData;
}

/**
 * Injects Schema.org SoftwareApplication + FAQPage structured data
 * as JSON-LD script tags into the document head for Google rich snippets.
 */
export default function ProductStructuredData({ product }: Props) {
  useEffect(() => {
    const baseUrl = window.location.origin;
    const pageUrl = window.location.href;

    const softwareAppSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: product.name,
      description: product.shortDescription,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: pageUrl,
      },
      featureList: product.features.map((f) => f.title).join(", "),
      screenshot: product.screenshots.map((s) => ({
        "@type": "ImageObject",
        caption: s.label,
        contentUrl: s.image,
      })),
      author: {
        "@type": "Organization",
        name: "StackMap Systems",
        url: baseUrl,
      },
      url: pageUrl,
      datePublished: "2025-01-01",
      dateModified: "2026-06-10",
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: product.features.map((f) => ({
        "@type": "Question",
        name: `What is ${f.title}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.description,
        },
      })),
    };

    const softwareScript = document.createElement("script");
    softwareScript.type = "application/ld+json";
    softwareScript.id = "schema-software";
    softwareScript.textContent = JSON.stringify(softwareAppSchema);
    document.head.appendChild(softwareScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "schema-faq";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const s = document.getElementById("schema-software");
      const f = document.getElementById("schema-faq");
      if (s) s.remove();
      if (f) f.remove();
    };
  }, [product]);

  return null;
}